/**
 * 对比视图：双指捏合缩放（iPad Safari），与桌面滚轮共用 zoomLevel 区间。
 * 需在 touchmove 上使用 passive: false 以便 preventDefault。
 */

function touchDistance(tl: TouchList): number {
  if (tl.length < 2) return 0;
  const dx = tl[0].clientX - tl[1].clientX;
  const dy = tl[0].clientY - tl[1].clientY;
  return Math.hypot(dx, dy);
}

export type ComparePanelPinchZoomOptions = {
  getZoom: () => number;
  setZoom: (z: number) => void;
  minZoom?: number;
  maxZoom?: number;
  /** zoom ≤ 1 时重置平移 */
  resetPan?: () => void;
  /** 返回 true 则不处理捏合（例如在滑杆上） */
  shouldIgnoreTouchTarget?: (target: EventTarget | null) => boolean;
};

export function attachComparePanelPinchZoom(
  container: HTMLElement,
  options: ComparePanelPinchZoomOptions,
): () => void {
  const min = options.minZoom ?? 0.5;
  const max = options.maxZoom ?? 5;

  let anchorDist = 0;
  let anchorZoom = 1;

  const resetAnchor = () => {
    anchorDist = 0;
  };

  const tryBeginAnchor = (e: TouchEvent) => {
    if (e.touches.length !== 2) return;
    if (options.shouldIgnoreTouchTarget?.(e.target)) return;
    const d = touchDistance(e.touches);
    if (d < 12) return;
    anchorDist = d;
    anchorZoom = options.getZoom();
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) tryBeginAnchor(e);
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 2) return;
    if (options.shouldIgnoreTouchTarget?.(e.target)) return;
    if (anchorDist < 12) {
      tryBeginAnchor(e);
      return;
    }
    const d = touchDistance(e.touches);
    const ratio = d / anchorDist;
    let z = anchorZoom * ratio;
    z = Math.max(min, Math.min(max, Math.round(z * 100) / 100));
    options.setZoom(z);
    e.preventDefault();
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (e.touches.length < 2) {
      resetAnchor();
      if (options.getZoom() <= 1) options.resetPan?.();
    }
  };

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd);
  container.addEventListener('touchcancel', onTouchEnd);

  return () => {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchmove', onTouchMove);
    container.removeEventListener('touchend', onTouchEnd);
    container.removeEventListener('touchcancel', onTouchEnd);
  };
}
